import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import ThemeToggle from './ui/ThemeToggle';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/#', label: t('nav.product') },
    { to: '/#pricing', label: t('nav.pricing') },
    { to: '/#product-tour', label: t('nav.demo') },
    { to: '/#contact', label: t('nav.contact') },
  ];

  return (
    <nav className="fixed top-0 w-full bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl z-50 border-b border-slate-200/50 dark:border-gray-700/50 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
              Sustainalyze
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-slate-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <LanguageSelector />
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="text-slate-600 hover:text-emerald-600 font-medium transition-colors">
                  {t('nav.dashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-emerald-600 border border-emerald-200 px-4 py-2 rounded-xl hover:bg-emerald-50 transition-all duration-200 font-medium"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('nav.signout')}
                </button>
              </>
            ) : (
              <Link
                to="/signin"
                className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-2 rounded-xl hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200 font-semibold"
              >
                {t('nav.signin')}
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-green-800"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="block px-3 py-2 text-gray-700 dark:text-gray-300 hover:text-green-800 dark:hover:text-emerald-400"
              >
                {link.label}
              </Link>
            ))}
            <div className="px-3 py-2 flex items-center gap-2">
              <LanguageSelector />
              <ThemeToggle />
            </div>
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 text-gray-700 hover:text-green-800"
                >
                  {t('nav.dashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center text-green-800 border border-green-800 px-4 py-2 rounded-lg hover:bg-green-50"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('nav.signout')}
                </button>
              </>
            ) : (
              <Link
                to="/signin"
                className="block w-full text-center bg-green-800 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                {t('nav.signin')}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
